"use client"

import styled from "@emotion/styled"
import { Box } from "@mui/material"
import Image from "next/image"
import { extractProperty, extractPropertyMediaItems, getUmbracoMediaUrl } from "utils"
import PortContainer from "./PortContainer"

const StyledSection = styled.section<{ $darkMode?: boolean }>`
  --p-bg: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBackgroundColor
      : props.theme.colors.port.lightBackgroundColor};
  --p-text: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkTextColor
      : props.theme.colors.port.lightTextColor};
  --p-border-color: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBorderColor
      : props.theme.colors.port.lightBorderColor};
  --p-border-color-hover: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBorderColor
      : props.theme.colors.port.defaultTextColor};
  --p-item-bg: ${(props) => (props.$darkMode ? "rgba(255, 255, 255, 0.05)" : "transparent")};
  --p-item-bg-hover: transparent;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
  gap: 64px;
  background-color: var(--p-bg);
  color: var(--p-text);
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 120px 0;
  }
`

const StyledContent = styled.div`
  width: 100%;
  display: grid;
  gap: 40px;
  grid-template-columns: 100%;
  min-width: fit-content;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 1fr 54%;
    align-items: center;
  }
`

const StyledImage = styled(Image)`
  width: 100%;
  height: 202px;
  object-fit: cover;
  border-radius: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    object-fit: cover;
    border-radius: 12px;
    height: 336px;
    max-width: 556px;
  }
`

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  ${(props) => props.theme.breakpoints.up("lg")} {
    gap: 40px;
  }
`

const StyledListItem = styled.li`
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

const StyledIcon = styled(Image)`
  object-fit: contain;
  width: 50px;
  height: 50px;
`

const StyledTitle = styled.h4`
  margin: 0;
  padding: 0;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%; /* 30px */
  letter-spacing: 0.6px;
  text-transform: capitalize;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
  }
`
const StyledP = styled.p`
  margin: 0;
  padding: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`

type ItemType = {
  title: string
  text: string
  icon: string
}
type PortFeaturesProps = {
  data: any
}

const PortFeatures = ({ data }: PortFeaturesProps) => {
  const image = extractProperty(data, "image")
  const darkMode = extractProperty(data, "darkMode") || false

  const itemsList = extractProperty(data, "itemsList")

  const items: ItemType[] =
    itemsList?.map((item: any) => ({
      title: extractProperty(item, "title"),
      text: extractProperty(item, "text"),
      icon: extractPropertyMediaItems(item, "icon")?.[0],
    })) || []

  // in case data fields are empty, return null
  if ((!items || items.length === 0) && !image) {
    return null
  }

  return (
    <StyledSection id="port-features" $darkMode={!!darkMode}>
      <PortContainer paddingInline={20}>
        <StyledContent>
          {items && items.length > 0 && (
            <StyledList>
              {items.map((item, index) => (
                <StyledListItem key={index}>
                  {item.icon && (
                    <StyledIcon
                      src={getUmbracoMediaUrl(item.icon)}
                      alt={item.title || `Feature Icon ${index + 1}`}
                      width={50}
                      height={50}
                      style={{ filter: darkMode ? "brightness(0) invert(1)" : "none" }}
                    />
                  )}
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <StyledTitle>{item.title}</StyledTitle>
                    <StyledP>{item.text}</StyledP>
                  </Box>
                </StyledListItem>
              ))}
            </StyledList>
          )}
          {image && (
            <StyledImage
              src={getUmbracoMediaUrl(image)}
              alt={"Feature Image"}
              width={556}
              height={336}
              priority
            />
          )}
        </StyledContent>
      </PortContainer>
    </StyledSection>
  )
}
export default PortFeatures
